import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AdminApplicationStatuses,
  ApplicationWithAdminData,
  PaginationData,
  StatusConfig,
} from '../../types/admin';
import { User } from '../../types/auth';
import { canUserPerformAction } from '../../utils/permissionUtils';
import { canUserRevertStatus } from '../../utils/revertUtils';
import Button from 'shared/components/UC/Button';
import Pagination from 'shared/components/Pagination';
import styles from './ApplicationListTable.module.css';
import ApplicationStatusBadge from './ApplicationStatusBadge';

interface ApplicationListTableProps {
  applications: ApplicationWithAdminData[];
  groupByStatus?: boolean;
  onAssignReview?: (applicationId: string) => void;
  onMarkAdmissible?: (applicationId: string) => void;
  onMarkNotAdmissible?: (applicationId: string) => void;
  onMarkEvaluated?: (applicationId: string) => void;
  onMarkSelected?: (applicationId: string) => void;
  onMarkNotSelected?: (applicationId: string) => void;
  onRevertStatus?: (applicationId: string) => void;
  onManageEvaluators?: (application: ApplicationWithAdminData) => void;
  onViewEvaluations?: (application: ApplicationWithAdminData) => void;
  onDownloadPdf?: (applicationId: string) => void;
  currentUserId?: string;
  currentUser?: User | null;
  isLoading?: boolean;
  statusPagination?: Record<string, PaginationData>;
  onStatusPageChange?: (status: string, page: number) => void;
  onStatusPageSizeChange?: (status: string, pageSize: number) => void;
  onExportStatus?: (status: string) => void;
  searchTerm?: string;
  visibleStatuses?: string[];
}

interface ActionsMenuProps {
  application: ApplicationWithAdminData;
  onAssignReview?: (applicationId: string) => void;
  onMarkAdmissible?: (applicationId: string) => void;
  onMarkNotAdmissible?: (applicationId: string) => void;
  onMarkEvaluated?: (applicationId: string) => void;
  onMarkSelected?: (applicationId: string) => void;
  onMarkNotSelected?: (applicationId: string) => void;
  onRevertStatus?: (applicationId: string) => void;
  onManageEvaluators?: (application: ApplicationWithAdminData) => void;
  onViewEvaluations?: (application: ApplicationWithAdminData) => void;
  onDownloadPdf?: (applicationId: string) => void;
  onViewDetails?: (applicationId: string) => void;
  currentUserId?: string;
  currentUser?: User | null;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  application,
  onAssignReview,
  onMarkAdmissible,
  onMarkNotAdmissible,
  onMarkEvaluated,
  onMarkSelected,
  onMarkNotSelected,
  onRevertStatus,
  onManageEvaluators,
  onViewEvaluations,
  onDownloadPdf,
  onViewDetails,
  currentUserId,
  currentUser,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position when opening and update on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom,
          left: rect.right,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const canAssignReview = () => {
    return (
      application.status === 'submitted' &&
      !application.reviewerAssignment &&
      currentUser &&
      canUserPerformAction('assignReview', currentUser, application)
    );
  };

  const canEvaluateAdmissibility = () => {
    return (
      application.status === 'under_admissibility_review' &&
      application.reviewerAssignment?.adminId === currentUserId &&
      currentUser &&
      canUserPerformAction('evaluateAdmissibility', currentUser, application)
    );
  };

  const canRevert = () => {
    return currentUser && canUserRevertStatus(application, currentUser);
  };

  const canManageEvaluators = () => {
    return (
      (application.status === 'admissible' ||
        application.status === 'under_evaluation' ||
        application.status === 'evaluated') &&
      onManageEvaluators &&
      currentUser &&
      canUserPerformAction('manageEvaluators', currentUser, application)
    );
  };

  const canViewEvaluations = () => {
    return (
      (application.status === 'under_evaluation' ||
        application.status === 'evaluated'||
        application.status === 'selected'||
        application.status === 'not_selected') &&
      onViewEvaluations &&
      currentUser &&
      canUserPerformAction('viewEvaluations', currentUser, application)
    );
  };

  const canMarkEvaluated = () => {
    return (
      (application.status === 'admissible' ||
        application.status === 'under_evaluation') &&
      onMarkEvaluated &&
      currentUser &&
      canUserPerformAction('markEvaluated', currentUser, application)
    );
  };

  const canMarkAwardStatus = () => {
    return (
      application.status === 'evaluated' &&
      (onMarkSelected || onMarkNotSelected) &&
      currentUser &&
      currentUser.role === 'admin' &&
      currentUser.userProfile?.affiliation === 'TODAS'
    );
  };

  const actions: { label: string; handler: () => void }[] = [];

  if (canAssignReview() && onAssignReview)
    actions.push({ label: 'Marcar como en revisión', handler: () => onAssignReview(application.id) });
  if (canEvaluateAdmissibility()) {
    if (onMarkAdmissible)
      actions.push({ label: 'Marcar Admisible', handler: () => onMarkAdmissible(application.id) });
    if (onMarkNotAdmissible)
      actions.push({ label: 'Marcar No Admisible', handler: () => onMarkNotAdmissible(application.id) });
  }
  if (canManageEvaluators() && onManageEvaluators)
    actions.push({ label: 'Gestionar Evaluadores', handler: () => onManageEvaluators(application) });
  if (canViewEvaluations() && onViewEvaluations)
    actions.push({ label: 'Ver evaluaciones', handler: () => onViewEvaluations(application) });
  if (canMarkEvaluated() && onMarkEvaluated)
    actions.push({ label: 'Marcar como Evaluada', handler: () => onMarkEvaluated(application.id) });
  if (canMarkAwardStatus()) {
    if (onMarkSelected)
      actions.push({ label: 'Marcar como Adjudicada', handler: () => onMarkSelected(application.id) });
    if (onMarkNotSelected)
      actions.push({ label: 'Marcar como No Adjudicada', handler: () => onMarkNotSelected(application.id) });
  }
  if (onDownloadPdf)
    actions.push({ label: 'Descargar PDF', handler: () => onDownloadPdf(application.id) });
  if (canRevert() && onRevertStatus)
    actions.push({ label: 'Revertir Estado', handler: () => onRevertStatus(application.id) });

  if (actions.length === 0) return null;

  if (actions.length === 1) {
    return (
      <Button
        variant="text"
        size="sm"
        className={styles.actionLink}
        onClick={actions[0].handler}
      >
        <i className="uc-icon icon-size--sm">download</i>
        {actions[0].label}
      </Button>
    );
  }

  return (
    <div className={styles.actionsMenuContainer} ref={buttonRef}>
      <div ref={menuRef}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className={styles.actionButton}
        >
          Acciones
          <i className="uc-icon icon-size--sm" style={{ marginLeft: '0.25rem' }}>expand_more</i>
        </Button>
        {isOpen && dropdownPosition && (
          <div
            className={styles.actionsMenuDropdown}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              transform: 'translateX(-100%)',
            }}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                className={styles.actionsMenuItem}
                onClick={() => { setIsOpen(false); action.handler(); }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ApplicationGroupProps {
  status: string;
  applications: ApplicationWithAdminData[];
  onAssignReview?: (applicationId: string) => void;
  onMarkAdmissible?: (applicationId: string) => void;
  onMarkNotAdmissible?: (applicationId: string) => void;
  onMarkEvaluated?: (applicationId: string) => void;
  onMarkSelected?: (applicationId: string) => void;
  onMarkNotSelected?: (applicationId: string) => void;
  onRevertStatus?: (applicationId: string) => void;
  onManageEvaluators?: (application: ApplicationWithAdminData) => void;
  onViewEvaluations?: (application: ApplicationWithAdminData) => void;
  onDownloadPdf?: (applicationId: string) => void;
  currentUserId?: string;
  currentUser?: User | null;
  pagination?: PaginationData;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onExportStatus?: (status: string) => void;
}

const ApplicationGroup: React.FC<ApplicationGroupProps> = ({
  status,
  applications,
  onAssignReview,
  onMarkAdmissible,
  onMarkNotAdmissible,
  onMarkEvaluated,
  onMarkSelected,
  onMarkNotSelected,
  onRevertStatus,
  onManageEvaluators,
  onViewEvaluations,
  onDownloadPdf,
  currentUserId,
  currentUser,
  pagination,
  onPageChange,
  onPageSizeChange,
  onExportStatus,
}) => {
  const navigate = useNavigate();
  const handleViewDetails = (applicationId: string) => {
    navigate(`/admin/application/${applicationId}`);
  };
  const handleOpenNewTab = (applicationId: string) => {
    window.open(`/admin/application/${applicationId}`, '_blank');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No enviado';
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (dateString: string | null) => {
    if (!dateString) return 'No especificada';
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Santiago',
    });
  };

  const getUnidadAcademica = (app: ApplicationWithAdminData) => {
    return app.user.userProfile?.unidadAcademica || 'No especificada';
  };

  const getEvaluatorStatus = (assignment: any) => {
    const isConfirmed = assignment.evaluator.isConfirmed;
    const hasEvaluation = !!assignment.evaluation;
    const evaluationSubmittedAt = assignment.evaluation?.submittedAt;

    if (!isConfirmed) {
      return { label: 'Por confirmar', color: 'gray' };
    }
    if (isConfirmed && !hasEvaluation) {
      return { label: 'Confirmado', color: 'yellow' };
    }
    if (isConfirmed && hasEvaluation && !evaluationSubmittedAt) {
      return { label: 'Evaluando', color: 'blue' };
    }
    if (isConfirmed && hasEvaluation && evaluationSubmittedAt) {
      return { label: 'Evaluado', color: 'green' };
    }
    return { label: 'Desconocido', color: 'gray' };
  };

  const shouldShowEvaluatorsColumn = (status: string) => {
    const evaluatorStatuses = ['admissible'];
    return evaluatorStatuses.includes(status);
  };

  const shouldShowDateColumn = (status: string) => {
    const dateStatuses = [
      'submitted',
      'under_admissibility_review',
      'admissible',
      'not_admissible',
    ];
    return dateStatuses.includes(status);
  };

  const shouldShowEvaluationDataColumns = (status: string) => {
    return status === 'under_evaluation' || status === 'evaluated';
  };

  const shouldShowAwardColumns = (status: string) => {
    return status === 'selected' || status === 'not_selected';
  };

  const isSuperAdmin = () => {
    return (
      currentUser?.role === 'admin' &&
      currentUser?.userProfile?.affiliation === 'TODAS'
    );
  };

  return (
    <div className={styles.statusGroup}>
      <div className={styles.statusHeader}>
        <div className={styles.statusInfo}>
          <ApplicationStatusBadge status={status} />
          <span className={styles.count}>({pagination?.total || 0})</span>
        </div>
        {onExportStatus && (
          <Button
            variant="text"
            size="sm"
            onClick={() => onExportStatus(status)}
          >
            <i className="uc-icon icon-size--sm">download</i>
            Descargar CSV
          </Button>
        )}
      </div>


      <div className={styles.tableContainer}>
        {applications.length === 0 ? (
          <div className={styles.emptyState}>
            <p>
              No hay aplicaciones que coincidan con los filtros seleccionados.
            </p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Folio</th>
                <th>Investigador Responsable</th>
                {status === 'draft' && <th>Fecha de Creación</th>}
                {status !== 'draft' &&
                  !shouldShowEvaluatorsColumn(status) &&
                  shouldShowDateColumn(status) && <th>Fecha de Envío</th>}
                <th>Unidad Académica</th>
                {status !== 'draft' &&
                  !shouldShowEvaluationDataColumns(status) && (
                    <th>Revisor Admisibilidad</th>
                  )}
                {shouldShowEvaluatorsColumn(status) &&
                  !shouldShowEvaluationDataColumns(status) && (
                    <th>Evaluadores</th>
                  )}
                {shouldShowEvaluationDataColumns(status) && (
                  <>
                    <th>Evaluaciones recibidas / Evaluadores asignados</th>
                    <th>Puntaje Promedio</th>
                    <th>Diferencia de puntajes</th>
                  </>
                )}
                {shouldShowAwardColumns(status) && isSuperAdmin() && (
                  <th>Puntaje Promedio</th>
                )}
                {shouldShowAwardColumns(status) && (
                  <th>Notificación a postulante</th>
                )}
                <th className={styles.actionsHeader}></th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id} className={styles.row}>
                  <td className={styles.folio}>
                    <div className={styles.folioCell}>
                      <button
                        onClick={() => handleViewDetails(app.id)}
                        className={styles.folioLink}
                      >
                        {app.applicationNumber}
                      </button>
                      <button
                        onClick={() => handleOpenNewTab(app.id)}
                        className={styles.folioNewTab}
                        aria-label="Abrir en pestaña nueva"
                        title="Abrir en pestaña nueva"
                      >
                        <i className="uc-icon icon-size--sm" aria-hidden="true">
                          open_in_new
                        </i>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className={styles.applicant}>
                      <div className={styles.applicantName}>
                        {app.user.name}
                      </div>
                      <div className={styles.applicantEmail}>
                        {app.user.email}
                      </div>
                    </div>
                  </td>
                  {status === 'draft' && (
                    <td className={styles.date}>
                      {formatDateOnly(app.createdAt)}
                    </td>
                  )}
                  {status !== 'draft' &&
                    !shouldShowEvaluatorsColumn(status) &&
                    shouldShowDateColumn(status) && (
                      <td className={styles.date}>
                        {formatDate(app.submittedAt)}
                      </td>
                    )}
                  <td>{getUnidadAcademica(app)}</td>
                  {status !== 'draft' &&
                    !shouldShowEvaluationDataColumns(status) && (
                      <td>
                        {app.reviewerAssignment ? (
                          <div className={styles.reviewer}>
                            <div className={styles.reviewerName}>
                              {app.reviewerAssignment.adminName}
                            </div>
                            <div className={styles.assignedDate}>
                              {formatDate(app.reviewerAssignment.assignedAt)}
                            </div>
                          </div>
                        ) : (
                          <span className={styles.noReviewer}>Sin asignar</span>
                        )}
                      </td>
                    )}
                  {shouldShowEvaluatorsColumn(status) && (
                    <td>
                      {app.evaluatorAssignments &&
                      app.evaluatorAssignments.length > 0 ? (
                        <div className={styles.evaluatorsList}>
                          {app.evaluatorAssignments.map((assignment) => {
                            const statusInfo = getEvaluatorStatus(assignment);
                            return (
                              <div
                                key={assignment.id}
                                className={styles.evaluatorItem}
                              >
                                <span
                                  className={`${styles.statusChip} ${
                                    styles[
                                      `statusChip${
                                        statusInfo.color
                                          .charAt(0)
                                          .toUpperCase() +
                                        statusInfo.color.slice(1)
                                      }`
                                    ]
                                  }`}
                                  title={statusInfo.label}
                                >
                                  {statusInfo.label}
                                </span>
                                <span className={styles.evaluatorName}>
                                  {assignment.evaluator.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className={styles.noEvaluators}>
                          Sin evaluadores
                        </span>
                      )}
                    </td>
                  )}
                  {shouldShowEvaluationDataColumns(status) && (
                    <>
                      <td className={styles.evaluationData}>
                        {app.evaluationSummary?.numberOfEvaluations ?? 0} /{' '}
                        {app.evaluationSummary?.numberOfEvaluators ?? 0}
                      </td>
                      <td className={styles.evaluationData}>
                        {app.evaluationSummary?.weightedScoreDistribution
                          .average
                          ? app.evaluationSummary.weightedScoreDistribution.average.toFixed(
                              2
                            )
                          : 'N/A'}
                      </td>
                      <td className={styles.evaluationData}>
                        {app.evaluationSummary?.weightedScoreDistribution
                          .max !== undefined &&
                        app.evaluationSummary?.weightedScoreDistribution.min !==
                          undefined
                          ? (
                              app.evaluationSummary.weightedScoreDistribution
                                .max -
                              app.evaluationSummary.weightedScoreDistribution
                                .min
                            ).toFixed(2)
                          : 'N/A'}
                      </td>
                    </>
                  )}
                  {shouldShowAwardColumns(status) && isSuperAdmin() && (
                    <td className={styles.evaluationData}>
                      {app.evaluationSummary?.weightedScoreDistribution.average
                        ? app.evaluationSummary.weightedScoreDistribution.average.toFixed(
                            2
                          )
                        : 'N/A'}
                    </td>
                  )}
                  {shouldShowAwardColumns(status) && (
                    <td className={styles.date}>
                      {app.awardNotifiedAt
                        ? formatDate(app.awardNotifiedAt)
                        : 'No notificado'}
                    </td>
                  )}
                  <td className={styles.actionsCell}>
                    <ActionsMenu
                      application={app}
                      onAssignReview={onAssignReview}
                      onMarkAdmissible={onMarkAdmissible}
                      onMarkNotAdmissible={onMarkNotAdmissible}
                      onMarkEvaluated={onMarkEvaluated}
                      onMarkSelected={onMarkSelected}
                      onMarkNotSelected={onMarkNotSelected}
                      onRevertStatus={onRevertStatus}
                      onManageEvaluators={onManageEvaluators}
                      onViewEvaluations={onViewEvaluations}
                      onDownloadPdf={onDownloadPdf}
                      onViewDetails={handleViewDetails}
                      currentUserId={currentUserId}
                      currentUser={currentUser}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination && onPageChange && (
        <Pagination
          pagination={pagination}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[10, 50, 100]}
        />
      )}
    </div>
  );
};

const ApplicationListTable: React.FC<ApplicationListTableProps> = ({
  applications,
  groupByStatus = true,
  onAssignReview,
  onMarkAdmissible,
  onMarkNotAdmissible,
  onMarkEvaluated,
  onMarkSelected,
  onMarkNotSelected,
  onRevertStatus,
  onManageEvaluators,
  onViewEvaluations,
  onDownloadPdf,
  currentUserId,
  currentUser,
  isLoading = false,
  statusPagination,
  onStatusPageChange,
  onStatusPageSizeChange,

  onExportStatus,
  searchTerm = '',
  visibleStatuses,
}) => {
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Cargando aplicaciones...</p>
      </div>
    );
  }

  if (!groupByStatus) {
    // Simple table without grouping - for future use
    return (
      <ApplicationGroup
        status="all"
        applications={applications}
        onAssignReview={onAssignReview}
        onMarkAdmissible={onMarkAdmissible}
        onMarkNotAdmissible={onMarkNotAdmissible}
        onMarkEvaluated={onMarkEvaluated}
        onMarkSelected={onMarkSelected}
        onMarkNotSelected={onMarkNotSelected}
        onRevertStatus={onRevertStatus}
        onManageEvaluators={onManageEvaluators}
        onViewEvaluations={onViewEvaluations}
        onDownloadPdf={onDownloadPdf}
        currentUserId={currentUserId}
        currentUser={currentUser}
        onExportStatus={onExportStatus}
      />
    );
  }
  const baseGroupByStatus = AdminApplicationStatuses.reduce((acc, status) => {
    acc[status] = [];
    return acc;
  }, {} as Record<string, ApplicationWithAdminData[]>);

  // Group applications by status and sort by the defined order
  const groupedApplications = applications.reduce((groups, app) => {
    const status = app.status;
    if (!groups[status]) {
      groups[status] = [];
    }
    groups[status].push(app);
    return groups;
  }, baseGroupByStatus as Record<string, ApplicationWithAdminData[]>);

  // Sort status groups by defined order, only show visible statuses
  const sortedStatuses = (visibleStatuses || Object.keys(groupedApplications))
    .filter((status) => groupedApplications[status]) // Only include statuses that exist in grouped data
    .sort((a, b) => {
      const orderA = StatusConfig[a]?.order ?? 999;
      const orderB = StatusConfig[b]?.order ?? 999;
      return orderA - orderB;
    });

  return (
    <div className={styles.groupedTable}>
      {sortedStatuses.map((status) => {
        // Hide empty tables when there is an active search
        const hasApplications = groupedApplications[status].length > 0;
        if (!hasApplications && searchTerm.trim()) {
          return null;
        }

        return (
          <ApplicationGroup
            key={status}
            status={status}
            applications={groupedApplications[status]}
            onAssignReview={onAssignReview}
            onMarkAdmissible={onMarkAdmissible}
            onMarkNotAdmissible={onMarkNotAdmissible}
            onMarkEvaluated={onMarkEvaluated}
            onMarkSelected={onMarkSelected}
            onMarkNotSelected={onMarkNotSelected}
            onRevertStatus={onRevertStatus}
            onManageEvaluators={onManageEvaluators}
            onViewEvaluations={onViewEvaluations}
            onDownloadPdf={onDownloadPdf}
            currentUserId={currentUserId}
            currentUser={currentUser}
            pagination={statusPagination?.[status]}
            onPageChange={
              onStatusPageChange
                ? (page) => onStatusPageChange(status, page)
                : undefined
            }
            onPageSizeChange={
              onStatusPageSizeChange
                ? (pageSize) => onStatusPageSizeChange(status, pageSize)
                : undefined
            }
            onExportStatus={onExportStatus}
          />
        );
      })}
    </div>
  );
};

export default ApplicationListTable;
